import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { canTransition, TABLE_TRANSITIONS } from '@/lib/domain';
import type { TableState } from '@/lib/shared-types';
import { usePermission } from '@/Hooks/usePermission';
import { useRequireScope } from '@/Hooks/useRequireScope';
import type { LiveTable } from '../lib/live-table';
import { ContextMenu, type MenuAnchor, type MenuRow } from './ContextMenu';

export type { MenuAnchor };

export type TableAction =
  | 'openSession'
  | 'addOrder'
  | 'viewSession'
  | 'requestBill'
  | 'viewChecks'
  | 'closeSession'
  | 'moveSession'
  | 'markAvailable';

interface Props {
  table: LiveTable;
  anchor: MenuAnchor;
  /** Null while the session is still unknown, so the menu never lies. */
  hasSession: boolean | null;
  /**
   * Whether this operator may read the table's session at all. It is the reason
   * `hasSession` can stay null forever, and the two cases need different words.
   */
  canReadSession: boolean;
  onPick: (action: TableAction) => void;
  onClose: () => void;
}

interface TableMenuRow extends MenuRow {
  action: TableAction;
}

/**
 * §5.2 — the actions for a table live on the table.
 *
 * The shell (positioning, dismissal, touch behaviour) is `ContextMenu`, shared
 * with every other thing on the map. What is here is only the part that is
 * about tables: every entry mirrors what the API would actually accept — the
 * permission key it enforces (§24.3) and, for anything that moves the table,
 * `TABLE_TRANSITIONS` (§7), the same machine `PATCH /tables/:id/state` asserts
 * on. An entry that leads to a guaranteed 403/422 and an entry missing for a
 * move the server would have taken are the same bug seen from two sides.
 */
export function TableContextMenu({
  table,
  anchor,
  hasSession,
  canReadSession,
  onPick,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const inScope = useRequireScope();

  /**
   * §24.4 — the *where* half, which this menu was not asking. `PATCH
   * /tables/:id/state` declares `@RequireScope(TABLE,'id')` and `POST /sessions`
   * declares `@RequireScope(TABLE,'tableId')`, but the live map is drawn from
   * list routes that carry no scope at all — so a floor-scoped operator sees
   * every table in the tenant and, before this, a live "Open session" on tables
   * the server answers `Out of scope for TABLE` for. Permission keys cannot
   * catch it: `sessions.create` held at Floor-1 is still `sessions.create`.
   */
  const tableOutOfScope = inScope('TABLE', table.id) ? null : t('spaces.menu.tableOutOfScope');

  const { items, footnote } = useMemo<{ items: TableMenuRow[]; footnote: string | null }>(() => {
    const state = table.state as TableState;

    // The session is what most of this menu acts on. Not knowing it yet and not
    // being allowed to read it are different problems: the first is a moment's
    // wait, the second is a role that will never see these rows, so it is said
    // once at the foot of the menu instead of on every entry.
    const pending = canReadSession && hasSession === null;
    const pendingWhy = pending ? t('spaces.menu.sessionPending') : undefined;

    /**
     * `SessionsService.assertTableAvailable()` refuses BLOCKED and
     * OUT_OF_SERVICE by name; every other state either seats directly or is
     * reset through AVAILABLE first, which is what `moveTable(strict)` does.
     */
    const seatable =
      state !== 'BLOCKED' &&
      state !== 'OUT_OF_SERVICE' &&
      (canTransition(TABLE_TRANSITIONS, state, 'OCCUPIED') ||
        canTransition(TABLE_TRANSITIONS, state, 'AVAILABLE'));

    // `PATCH /tables/:id/state` treats a move to the state we are already in as
    // a no-op, so AVAILABLE is the one state with nothing to free.
    const freeable = canTransition(TABLE_TRANSITIONS, state, 'AVAILABLE');

    const rows: TableMenuRow[] = [];

    // Ordering is the point of the whole feature, so it sits at the top for a
    // table that can take one. On a free table it opens the session first —
    // the operator asked to take an order, not to run a two-step ritual, but
    // that first call is `POST /sessions` and needs its own permission.
    const opensSessionFirst = hasSession !== true;
    const canOpenSession = can('sessions.create');
    // `QuickOrderDialog` opens the session itself when the table has none, so
    // the scope on `POST /sessions` blocks this entry exactly as the missing
    // permission does — same call, same refusal.
    const sessionBlock = !canOpenSession ? t('spaces.menu.needsSessionCreate') : tableOutOfScope;
    if (can('orders.create') && (hasSession === true || seatable)) {
      rows.push({
        action: 'addOrder',
        label: t('spaces.menu.addOrder'),
        disabled: opensSessionFirst && Boolean(sessionBlock),
        reason: opensSessionFirst ? (sessionBlock ?? undefined) : undefined,
      });
    }

    // Opening a second session on a table the server already has one for
    // silently hands back the first, so it is only offered when there is none.
    if (seatable && canOpenSession && hasSession !== true) {
      rows.push({
        action: 'openSession',
        label:
          state === 'RESERVED' ? t('spaces.menu.seatReservation') : t('spaces.menu.openSession'),
        disabled: pending || Boolean(tableOutOfScope),
        reason: tableOutOfScope ?? pendingWhy,
      });
    }

    // The session endpoints care about the session, not about the table state:
    // a PAYMENT_PENDING or even a CLEANING table with a live session is still
    // one whose bill can be read and whose session can be closed.
    if (canReadSession && hasSession !== false) {
      if (can('sessions.view')) {
        rows.push({
          action: 'viewSession',
          label: t('spaces.menu.viewSession'),
          disabled: pending,
          reason: pendingWhy,
        });
      }
      if (can('checks.view')) {
        rows.push({
          action: 'viewChecks',
          label: t('spaces.menu.viewChecks'),
          disabled: pending,
          reason: pendingWhy,
        });
      }
      // `POST /service-requests` carries no `@RequirePermissions` — it is the
      // guest "call a waiter" surface, so any authenticated operator may raise
      // one. The real precondition is the session id it is raised against.
      rows.push({
        action: 'requestBill',
        label: t('spaces.menu.requestBill'),
        disabled: pending,
        reason: pendingWhy,
      });
      // §7 — the visit moves, the bill and the orders come with it. The server
      // refuses unless the session is OPEN and the destination is free, so both
      // are re-checked there; here it is offered whenever a session exists.
      if (can('sessions.transfer')) {
        rows.push({
          action: 'moveSession',
          label: t('spaces.menu.moveSession'),
          disabled: pending,
          reason: pendingWhy,
        });
      }
      if (can('sessions.close')) {
        rows.push({
          action: 'closeSession',
          label: t('spaces.menu.closeSession'),
          tone: 'danger',
          disabled: pending,
          reason: pendingWhy,
        });
      }
    }

    if (freeable && can('tables.update')) {
      rows.push({
        action: 'markAvailable',
        label: t('spaces.menu.markAvailable'),
        disabled: Boolean(tableOutOfScope),
        // Out of scope is a refusal; the session note is a warning about a
        // state change the server *will* take — it does not close the visit,
        // and the bill would be left hanging on a table that reads as free.
        reason:
          tableOutOfScope ?? (hasSession === true ? t('spaces.menu.freeLeavesSession') : undefined),
      });
    }

    return {
      items: rows,
      footnote: canReadSession ? null : t('spaces.menu.sessionUnreadable'),
    };
  }, [can, canReadSession, hasSession, t, table.state, tableOutOfScope]);

  return (
    <ContextMenu
      title={t('spaces.menu.title', { number: table.number })}
      anchor={anchor}
      items={items}
      footnote={footnote}
      onPick={(item) => onPick(item.action)}
      onClose={onClose}
    />
  );
}
